import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  Link
} from '@mui/material';
import { Search, Edit as EditIcon, History as HistoryIcon, Close, Add, Delete as DeleteIcon, Map as MapIcon } from '@mui/icons-material';
import { format, subDays, startOfDay, endOfDay, startOfWeek, addDays, parseISO } from 'date-fns';
import { parseTimeEntryDate, formatTimeEntry, toDatetimeLocalForEntry } from '../utils/dateUtils';
import { timeService } from '../services/timeService';
import { employeeService } from '../services/employeeService';
import { workFunctionService } from '../services/workFunctionService';
import { useAuth } from '../hooks/useAuth';

const WEEK_STARTS_SUNDAY = 0;
const toYMD = (d) => (d ? format(d, 'yyyy-MM-dd') : '');
const toDatetimeLocal = (v) => toDatetimeLocalForEntry(v);
const googleMapsUrl = (lat, lon) => `https://www.google.com/maps?q=${Number(lat)},${Number(lon)}`;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// US state full name → abbreviation for location display
const US_STATE_TO_ABBR = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA', Colorado: 'CO',
  Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY',
  Louisiana: 'LA', Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH',
  'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
  Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT', Virginia: 'VA',
  Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY'
};
function formatLocationDisplay(str) {
  if (!str || typeof str !== 'string') return str;
  let s = str
    .replace(/, United States$/i, '')
    .replace(/, USA$/i, '')
    .trim();
  Object.keys(US_STATE_TO_ABBR).forEach((name) => {
    const re = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
    s = s.replace(re, US_STATE_TO_ABBR[name]);
  });
  return s.replace(/\s*,\s*,\s*/g, ', ').replace(/^\s*,\s*|\s*,\s*$/g, '').trim() || str;
}

const WF_OTHER = '__other__';
const emptyPunch = () => ({ clockIn: '', clockOut: '', workOrderNumber: '', workFunctionId: '', workFunctionOther: '' });
function getEmptyWeekRows() {
  return DAY_NAMES.map((day, i) => ({
    day,
    dayIndex: i,
    punches: [emptyPunch()]
  }));
}
function addPunchToDay(rows, dayIndex) {
  return rows.map((r, i) =>
    i === dayIndex ? { ...r, punches: [...r.punches, emptyPunch()] } : r
  );
}
function removePunchFromDay(rows, dayIndex, punchIndex) {
  const day = rows[dayIndex];
  if (!day || day.punches.length <= 1) return rows;
  return rows.map((r, i) =>
    i === dayIndex ? { ...r, punches: r.punches.filter((_, p) => p !== punchIndex) } : r
  );
}

const TimeEntries = () => {
  const { user } = useAuth();
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  const [dateFrom, setDateFrom] = useState(toYMD(startOfDay(subDays(new Date(), 6))));
  const [dateTo, setDateTo] = useState(toYMD(endOfDay(new Date())));
  const [employeeId, setEmployeeId] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [workFunctions, setWorkFunctions] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    clockIn: '',
    clockOut: '',
    hoursWorked: '',
    notes: '',
    workOrderNumber: '',
    workFunctionId: '',
    workFunctionOther: '',
    auditNotes: ''
  });
  const [editSaving, setEditSaving] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [manualWeekOpen, setManualWeekOpen] = useState(false);
  const [manualWeekDate, setManualWeekDate] = useState(() => toYMD(startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_SUNDAY })));
  const [manualWeekRows, setManualWeekRows] = useState(() => getEmptyWeekRows());
  const [manualWeekSaving, setManualWeekSaving] = useState(false);
  const [resolvingLocation, setResolvingLocation] = useState(null); // { id, which: 'in'|'out' }

  const fullName = (row) =>
    [row?.employee_first_name, row?.employee_last_name].filter(Boolean).join(' ') || '—';

  const formatTime = (v) => formatTimeEntry(v, 'MM/dd hh:mm a');

  const formatDate = (v) => {
    const d = parseTimeEntryDate(v);
    return d ? format(d, 'MM/dd/yyyy') : '—';
  };

  const loadEntries = async () => {
    if (!dateFrom || !dateTo) {
      setError('Select date range (From and To).');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const params = {
        dateFrom,
        dateTo,
        limit: 200,
        ...(employeeId ? { employeeId } : {})
      };
      const res = await timeService.getAll(params);
      const list = res.data ?? res?.data ?? [];
      setEntries(Array.isArray(list) ? list : []);
      setSelectedEntry(Array.isArray(list) && list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading time entries:', err);
      setError(err.response?.data?.message || 'Failed to load time entries');
      setEntries([]);
      setSelectedEntry(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [empRes, wfRes] = await Promise.all([
          employeeService.getAll({ activeOnly: 'true', limit: 500 }),
          workFunctionService.getAll().catch(() => ({ data: [] }))
        ]);
        setEmployees(empRes?.data ?? []);
        setWorkFunctions(wfRes?.data ?? []);
      } catch (e) {
        console.error('Error loading employees/functions:', e);
      }
    };
    load();
  }, []);

  useEffect(() => {
    loadEntries();
  }, []);

  const handleSearch = () => {
    loadEntries();
  };

  const handleResolveLocation = async (entryId, which) => {
    try {
      setResolvingLocation({ id: entryId, which });
      setError('');
      const res = await timeService.resolveLocation(entryId, which);
      if (res?.data && selectedEntry?.id === res.data.id) {
        setSelectedEntry(res.data);
      }
      await loadEntries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resolve location');
    } finally {
      setResolvingLocation(null);
    }
  };

  const openEditDialog = (row) => {
    if (!row) return;
    setEditRow(row);
    setEditForm({
      clockIn: toDatetimeLocal(row.clock_in),
      clockOut: toDatetimeLocal(row.clock_out),
      hoursWorked: row.hours_worked != null ? String(row.hours_worked) : '',
      notes: row.notes ?? '',
      workOrderNumber: row.work_order_number ?? '',
      workFunctionId:
        row.work_function_other && row.work_function_id == null ? WF_OTHER : row.work_function_id != null ? String(row.work_function_id) : '',
      workFunctionOther: row.work_function_other ?? '',
      auditNotes: ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editRow?.id) return;
    if (editForm.workFunctionId === WF_OTHER && !String(editForm.workFunctionOther || '').trim()) {
      setError('Enter a description when Function is Other.');
      return;
    }
    try {
      setEditSaving(true);
      setError('');
      const wfOtherPayload =
        editForm.workFunctionId === WF_OTHER ? String(editForm.workFunctionOther || '').trim() : '';
      await timeService.update(editRow.id, {
        clockIn: editForm.clockIn ? new Date(editForm.clockIn).toISOString() : undefined,
        clockOut: editForm.clockOut ? new Date(editForm.clockOut).toISOString() : undefined,
        hoursWorked: editForm.hoursWorked ? parseFloat(editForm.hoursWorked) : undefined,
        notes: editForm.notes || undefined,
        workOrderNumber: editForm.workOrderNumber || undefined,
        workFunctionId:
          editForm.workFunctionId === WF_OTHER ? '' : editForm.workFunctionId ? parseInt(editForm.workFunctionId, 10) : '',
        workFunctionOther: wfOtherPayload,
        auditNotes: isAdminOrManager ? editForm.auditNotes : undefined
      });
      setEditDialogOpen(false);
      setEditRow(null);
      await loadEntries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  const openAuditLog = async (entryId) => {
    setAuditLogOpen(true);
    setAuditLogLoading(true);
    try {
      const res = await timeService.getAuditLog(entryId);
      setAuditLog(res?.data ?? []);
    } catch (err) {
      setAuditLog([]);
      setError(err.response?.data?.message || 'Failed to load audit log');
    } finally {
      setAuditLogLoading(false);
    }
  };

  const openManualWeek = () => {
    const start = dateFrom ? parseISO(dateFrom) : new Date();
    const weekStart = startOfWeek(start, { weekStartsOn: WEEK_STARTS_SUNDAY });
    setManualWeekDate(toYMD(weekStart));
    setManualWeekRows(getEmptyWeekRows());
    setManualWeekOpen(true);
  };

  const handleSaveManualWeek = async () => {
    if (!employeeId) {
      setError('Select an employee first.');
      return;
    }
    const weekStart = parseISO(manualWeekDate);
    try {
      setManualWeekSaving(true);
      setError('');
      for (let i = 0; i < 7; i++) {
        const dayRow = manualWeekRows[i];
        const d = addDays(weekStart, i);
        const dateStr = toYMD(d);
        for (const p of dayRow.punches || []) {
          if (!p.clockIn || !p.clockOut) continue;
          const clockInVal = `${dateStr}T${p.clockIn.length === 5 ? p.clockIn : '08:00'}`;
          const clockOutVal = `${dateStr}T${p.clockOut.length === 5 ? p.clockOut : '17:00'}`;
          if (p.workFunctionId === WF_OTHER && !String(p.workFunctionOther || '').trim()) {
            setError('Enter a description for Function "Other" on each row that uses it.');
            setManualWeekSaving(false);
            return;
          }
          await timeService.create({
            employeeId,
            workOrderNumber: p.workOrderNumber || undefined,
            workFunctionId: p.workFunctionId === WF_OTHER ? '' : p.workFunctionId || undefined,
            workFunctionOther: p.workFunctionId === WF_OTHER ? String(p.workFunctionOther || '').trim() : '',
            clockIn: new Date(clockInVal).toISOString(),
            clockOut: new Date(clockOutVal).toISOString(),
            latitude_in: 0,
            longitude_in: 0,
            device_name: 'Manual entry'
          });
        }
      }
      setManualWeekOpen(false);
      await loadEntries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save manual time entries');
    } finally {
      setManualWeekSaving(false);
    }
  };

  const updateManualWeekPunch = (dayIndex, punchIndex, field, value) => {
    setManualWeekRows((prev) =>
      prev.map((r, i) => {
        if (i !== dayIndex) return r;
        const punches = (r.punches || [emptyPunch()]).map((p, pidx) =>
          pidx === punchIndex ? { ...p, [field]: value } : p
        );
        return { ...r, punches };
      })
    );
  };

  const totalHours = entries.reduce((sum, e) => sum + (Number(e.hours_worked) || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Time Entries
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdminOrManager && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={openManualWeek}
              disabled={!employeeId}
              title="Create time entries for selected employee (week template)"
            >
              Add week time
            </Button>
          )}
          <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => selectedEntry && openEditDialog(selectedEntry)} disabled={!selectedEntry}>
            Edit
          </Button>
          {isAdminOrManager && (
            <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={() => selectedEntry && openAuditLog(selectedEntry.id)} disabled={!selectedEntry}>
              History
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Selected Time Entry Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Employee</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedEntry ? (selectedEntry.employee_number ? `${selectedEntry.employee_number} — ` : '') + fullName(selectedEntry) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Date</Typography>
            <Typography variant="body2">
              {selectedEntry ? formatDate(selectedEntry.clock_in) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Work Order</Typography>
            <Typography variant="body2">
              {selectedEntry?.work_order_identifier ?? selectedEntry?.work_order_number ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Function</Typography>
            <Typography variant="body2">
              {selectedEntry?.function_label ?? selectedEntry?.function_code ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Clock-In Location</Typography>
            {selectedEntry?.location_in_text
              ? (selectedEntry?.latitude_in != null && selectedEntry?.longitude_in != null
                  ? (
                    <Link href={googleMapsUrl(selectedEntry.latitude_in, selectedEntry.longitude_in)} target="_blank" rel="noopener noreferrer" underline="hover">
                      {formatLocationDisplay(selectedEntry.location_in_text)}
                    </Link>
                    )
                  : formatLocationDisplay(selectedEntry.location_in_text))
              : (selectedEntry?.latitude_in != null && selectedEntry?.longitude_in != null)
                ? (resolvingLocation?.id === selectedEntry.id && resolvingLocation?.which === 'in'
                    ? 'Resolving…'
                    : (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleResolveLocation(selectedEntry.id, 'in')}
                          disabled={!!resolvingLocation}
                        >
                          Get location
                        </Button>
                        <Link href={googleMapsUrl(selectedEntry.latitude_in, selectedEntry.longitude_in)} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex' }} title="Open in Google Maps">
                          <MapIcon fontSize="small" />
                        </Link>
                      </Box>
                    ))
                : formatLocationDisplay(selectedEntry?.employee_location) ?? '—'}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Clock-Out Location</Typography>
            {selectedEntry?.location_out_text
              ? (selectedEntry?.latitude_out != null && selectedEntry?.longitude_out != null
                  ? (
                    <Link href={googleMapsUrl(selectedEntry.latitude_out, selectedEntry.longitude_out)} target="_blank" rel="noopener noreferrer" underline="hover">
                      {formatLocationDisplay(selectedEntry.location_out_text)}
                    </Link>
                    )
                  : formatLocationDisplay(selectedEntry.location_out_text))
              : (selectedEntry?.latitude_out != null && selectedEntry?.longitude_out != null)
                ? (resolvingLocation?.id === selectedEntry.id && resolvingLocation?.which === 'out'
                    ? 'Resolving…'
                    : (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleResolveLocation(selectedEntry.id, 'out')}
                          disabled={!!resolvingLocation}
                        >
                          Get location
                        </Button>
                        <Link href={googleMapsUrl(selectedEntry.latitude_out, selectedEntry.longitude_out)} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex' }} title="Open in Google Maps">
                          <MapIcon fontSize="small" />
                        </Link>
                      </Box>
                    ))
                : '—'}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Clock In</Typography>
            <Typography variant="body2">
              {selectedEntry ? formatTime(selectedEntry.clock_in) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Clock Out</Typography>
            <Typography variant="body2">
              {selectedEntry ? formatTime(selectedEntry.clock_out) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Hours</Typography>
            <Typography variant="body2">
              {selectedEntry?.hours_worked != null ? Number(selectedEntry.hours_worked).toFixed(2) : '—'}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 150 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Notes</Typography>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedEntry?.notes ?? '—'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedEntry(null)}
            disabled={!selectedEntry}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Date from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="Date to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Employee</InputLabel>
            <Select
              label="Employee"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.employee_number ? `${emp.employee_number} — ` : ''}{[emp.first_name, emp.last_name].filter(Boolean).join(' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={handleSearch} startIcon={<Search />}>
            Search
          </Button>
        </Box>
      </Paper>

      {/* Time entries table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Date</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Work Order</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Function</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Employee</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Clock In</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Clock-In Location</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Clock Out</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Clock-Out Location</Typography></TableCell>
              <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Hours</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No time entries. Set date range and employee (or leave empty for all), then Search.
                    {isAdminOrManager && employeeId && ' Use "Add week time" to create entries for this employee.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedEntry?.id === row.id}
                  onClick={() => setSelectedEntry(row)}
                  onDoubleClick={() => openEditDialog(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{formatDate(row.clock_in)}</TableCell>
                  <TableCell>{row.work_order_identifier || row.work_order_number || '—'}</TableCell>
                  <TableCell>{row.function_label || row.function_code || '—'}</TableCell>
                  <TableCell>
                    {row.employee_number ? `${row.employee_number} ` : ''}{fullName(row)}
                  </TableCell>
                  <TableCell>{formatTime(row.clock_in)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                      title={row.latitude_in != null && row.longitude_in != null ? `${Number(row.latitude_in).toFixed(6)}, ${Number(row.longitude_in).toFixed(6)} — Open in Google Maps` : ''}
                      disableHoverListener={!(row.latitude_in != null && row.longitude_in != null)}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {row.location_in_text
                          ? (row.latitude_in != null && row.longitude_in != null
                              ? (
                                <Link href={googleMapsUrl(row.latitude_in, row.longitude_in)} target="_blank" rel="noopener noreferrer" underline="hover" onClick={(e) => e.stopPropagation()}>
                                  {formatLocationDisplay(row.location_in_text)}
                                </Link>
                                )
                              : formatLocationDisplay(row.location_in_text))
                          : (row.latitude_in != null && row.longitude_in != null)
                            ? (resolvingLocation?.id === row.id && resolvingLocation?.which === 'in'
                                ? 'Resolving…'
                                : (
                                  <>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleResolveLocation(row.id, 'in')}
                                      disabled={!!resolvingLocation}
                                    >
                                      Get location
                                    </Button>
                                    <Link href={googleMapsUrl(row.latitude_in, row.longitude_in)} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex' }} onClick={(e) => e.stopPropagation()} title="Open in Google Maps">
                                      <MapIcon fontSize="small" />
                                    </Link>
                                  </>
                                ))
                            : formatLocationDisplay(row.employee_location) || '—'}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatTime(row.clock_out)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                      title={row.latitude_out != null && row.longitude_out != null ? `${Number(row.latitude_out).toFixed(6)}, ${Number(row.longitude_out).toFixed(6)} — Open in Google Maps` : ''}
                      disableHoverListener={!(row.latitude_out != null && row.longitude_out != null)}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {row.location_out_text
                          ? (row.latitude_out != null && row.longitude_out != null
                              ? (
                                <Link href={googleMapsUrl(row.latitude_out, row.longitude_out)} target="_blank" rel="noopener noreferrer" underline="hover" onClick={(e) => e.stopPropagation()}>
                                  {formatLocationDisplay(row.location_out_text)}
                                </Link>
                                )
                              : formatLocationDisplay(row.location_out_text))
                          : (row.latitude_out != null && row.longitude_out != null)
                            ? (resolvingLocation?.id === row.id && resolvingLocation?.which === 'out'
                                ? 'Resolving…'
                                : (
                                  <>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleResolveLocation(row.id, 'out')}
                                      disabled={!!resolvingLocation}
                                    >
                                      Get location
                                    </Button>
                                    <Link href={googleMapsUrl(row.latitude_out, row.longitude_out)} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex' }} onClick={(e) => e.stopPropagation()} title="Open in Google Maps">
                                      <MapIcon fontSize="small" />
                                    </Link>
                                  </>
                                ))
                            : '—'}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {row.hours_worked != null ? Number(row.hours_worked).toFixed(2) : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {entries.length > 0 && (
          <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Total hours: {totalHours.toFixed(2)}
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* Add week time (manual entry) dialog - same as in original Timecards */}
      <Dialog open={manualWeekOpen} onClose={() => !manualWeekSaving && setManualWeekOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add week time (create time entries)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Work week is Sunday–Saturday. Add one or more clock in/out pairs per day (e.g. start, lunch out/in, end of day). Leave a pair blank to skip. Creates new time entries for the selected employee.
          </Typography>
          {!employeeId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select an employee in the filters above, then click Add week time.
            </Alert>
          )}
          <TextField
            size="small"
            label="Week starting (Sunday)"
            type="date"
            value={manualWeekDate}
            onChange={(e) => setManualWeekDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, width: 200 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell>
                  <TableCell>Clock In</TableCell>
                  <TableCell>Clock Out</TableCell>
                  <TableCell>Work Order</TableCell>
                  <TableCell>Function</TableCell>
                  <TableCell padding="none" width={90}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {manualWeekRows.map((dayRow, dayIdx) =>
                  (dayRow.punches || [emptyPunch()]).map((punch, punchIdx) => (
                    <TableRow key={`${dayRow.day}-${punchIdx}`}>
                      <TableCell sx={{ fontWeight: 600, verticalAlign: 'middle' }}>
                        {punchIdx === 0 ? dayRow.day : ''}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="time"
                          value={punch.clockIn}
                          onChange={(e) => updateManualWeekPunch(dayIdx, punchIdx, 'clockIn', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          inputProps={{ step: 300 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="time"
                          value={punch.clockOut}
                          onChange={(e) => updateManualWeekPunch(dayIdx, punchIdx, 'clockOut', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          inputProps={{ step: 300 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={punch.workOrderNumber}
                          onChange={(e) => updateManualWeekPunch(dayIdx, punchIdx, 'workOrderNumber', e.target.value)}
                          placeholder="e.g. 500"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={punch.workFunctionId || ''}
                              displayEmpty
                              onChange={(e) =>
                                updateManualWeekPunch(dayIdx, punchIdx, 'workFunctionId', e.target.value)
                              }
                            >
                              <MenuItem value="">—</MenuItem>
                              {workFunctions.map((wf) => (
                                <MenuItem key={wf.id} value={String(wf.id)}>{wf.label}</MenuItem>
                              ))}
                              <MenuItem value={WF_OTHER}>Other…</MenuItem>
                            </Select>
                          </FormControl>
                          {punch.workFunctionId === WF_OTHER && (
                            <TextField
                              size="small"
                              placeholder="Describe function"
                              value={punch.workFunctionOther || ''}
                              onChange={(e) =>
                                updateManualWeekPunch(dayIdx, punchIdx, 'workFunctionOther', e.target.value)
                              }
                              fullWidth
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell padding="none" sx={{ verticalAlign: 'middle' }}>
                        <IconButton
                          size="small"
                          onClick={() => setManualWeekRows((prev) => addPunchToDay(prev, dayIdx))}
                          title="Add another punch (e.g. lunch)"
                          aria-label="Add punch"
                        >
                          <Add fontSize="small" />
                        </IconButton>
                        {(dayRow.punches?.length ?? 1) > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => setManualWeekRows((prev) => removePunchFromDay(prev, dayIdx, punchIdx))}
                            title="Remove this punch row"
                            aria-label="Remove punch"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualWeekOpen(false)} disabled={manualWeekSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveManualWeek} disabled={manualWeekSaving || !employeeId}>
            {manualWeekSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onClose={() => !editSaving && (setEditDialogOpen(false), setEditRow(null))} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Time Entry</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Clock In"
                type="datetime-local"
                value={editForm.clockIn}
                onChange={(e) => setEditForm((f) => ({ ...f, clockIn: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Clock Out"
                type="datetime-local"
                value={editForm.clockOut}
                onChange={(e) => setEditForm((f) => ({ ...f, clockOut: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Job # (Work Order)"
                value={editForm.workOrderNumber}
                onChange={(e) => setEditForm((f) => ({ ...f, workOrderNumber: e.target.value }))}
                placeholder="e.g. 500"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Function</InputLabel>
                <Select
                  value={editForm.workFunctionId}
                  label="Function"
                  onChange={(e) => setEditForm((f) => ({ ...f, workFunctionId: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {workFunctions.map((wf) => (
                    <MenuItem key={wf.id} value={String(wf.id)}>{wf.label}</MenuItem>
                  ))}
                  <MenuItem value={WF_OTHER}>Other…</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editForm.workFunctionId === WF_OTHER && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Function (other)"
                  value={editForm.workFunctionOther}
                  onChange={(e) => setEditForm((f) => ({ ...f, workFunctionOther: e.target.value }))}
                  placeholder="e.g. one-off task"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Hours"
                type="number"
                inputProps={{ step: 0.25, min: 0 }}
                value={editForm.hoursWorked}
                onChange={(e) => setEditForm((f) => ({ ...f, hoursWorked: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                rows={2}
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
            {isAdminOrManager && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Audit note (reason for edit)"
                  value={editForm.auditNotes}
                  onChange={(e) => setEditForm((f) => ({ ...f, auditNotes: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit log dialog */}
      <Dialog open={auditLogOpen} onClose={() => setAuditLogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit history</DialogTitle>
        <DialogContent>
          {auditLogLoading ? (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : auditLog.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
              No edit history for this time entry.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Who</TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell>Old</TableCell>
                    <TableCell>New</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLog.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.changed_at ? format(new Date(row.changed_at), 'MM/dd HH:mm') : '—'}</TableCell>
                      <TableCell>{[row.changed_by_first_name, row.changed_by_last_name].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell>{row.field_name || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.old_value ?? '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.new_value ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditLogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeEntries;
